import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import schoolsCsv from '../data/schools_utf_8.csv';
import { authAPI, getHomePathForUser, SignupPayload } from '../api/auth';
import { useAuth } from '../context/AuthContext';

type AuthTab = 'login' | 'signup';
type MemberRole = 'admin' | 'student';

type SchoolRecord = {
  code: string;
  schoolName: string;
  location: string;
};

const parseCsv = (csvText: string): SchoolRecord[] => {
  return csvText
    .split(/\r?\n/)
    .slice(1)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [code = '', schoolName = '', location = ''] = line.split(',');
      return {
        code: code.trim(),
        schoolName: schoolName.trim(),
        location: location.trim(),
      };
    })
    .filter((item) => item.code && item.schoolName && item.location);
};

const extractErrorMessage = (error: any): string => {
  return String(
    error?.response?.data?.message || error?.response?.data || error?.message || '알 수 없는 오류'
  );
};

const WelcomePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<AuthTab>('login');

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginMessage, setLoginMessage] = useState('');
  const [isLoginSubmitting, setIsLoginSubmitting] = useState(false);

  const [schools, setSchools] = useState<SchoolRecord[]>([]);
  const [role, setRole] = useState<MemberRole>('student');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [academyName, setAcademyName] = useState('');
  const [phone, setPhone] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [memberClass, setMemberClass] = useState('');
  const [memo, setMemo] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedSchoolCode, setSelectedSchoolCode] = useState('');
  const [signupMessage, setSignupMessage] = useState('');
  const [isSignupSubmitting, setIsSignupSubmitting] = useState(false);

  const { isAuthenticated, isLoading, login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const loadSchools = async () => {
      const response = await fetch(schoolsCsv);
      const csvText = await response.text();
      setSchools(parseCsv(csvText));
    };

    loadSchools().catch((error) => {
      console.error('학교 데이터를 불러오지 못했습니다.', error);
    });
  }, []);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate(getHomePathForUser(user), { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, user]);

  const locations = useMemo(
    () => Array.from(new Set(schools.map((school) => school.location))).sort((a, b) => a.localeCompare(b, 'ko')),
    [schools]
  );

  const filteredSchools = useMemo(
    () =>
      schools
        .filter((school) => school.location === selectedLocation)
        .sort((a, b) => a.schoolName.localeCompare(b.schoolName, 'ko')),
    [schools, selectedLocation]
  );

  const selectedSchoolRecord = filteredSchools.find((school) => school.code === selectedSchoolCode);

  const handleLoginSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isAuthenticated) {
      setLoginMessage('이미 로그인되어 있습니다. 홈 화면으로 이동합니다.');
      navigate(getHomePathForUser(user), { replace: true });
      return;
    }

    setLoginMessage('');
    setIsLoginSubmitting(true);

    try {
      const nextUser = await login(loginEmail, loginPassword);
      setLoginMessage('로그인 성공! 홈 화면으로 이동합니다.');
      navigate(getHomePathForUser(nextUser), { replace: true });
    } catch (error: any) {
      if (String(error?.message) === 'already_logged_in') {
        setLoginMessage('중복 로그인 방지: 이미 로그인 상태입니다.');
        navigate(getHomePathForUser(user), { replace: true });
      } else {
        setLoginMessage(`로그인 실패: ${extractErrorMessage(error)}`);
      }
    } finally {
      setIsLoginSubmitting(false);
    }
  };

  const handleSignupSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const payload = {
      role,
      email,
      name,
      password,
      academyName,
      phone,
      parentPhone,
      memberClass,
      memo,
      uniqueKey: `${phone}_${name}`,
      location: role === 'student' ? selectedLocation : '',
      schoolName: role === 'student' ? selectedSchoolRecord?.schoolName || '' : '',
      schoolCode: role === 'student' ? selectedSchoolRecord?.code || '' : '',
    };

    const signupPayload: SignupPayload = {
      email: payload.email,
      password: payload.password,
      name: payload.name,
      role: payload.role,
      phone: payload.phone,
      parentPhone: payload.role === 'student' ? payload.parentPhone : '',
      academyname: payload.academyName,
      uniqueKey: payload.uniqueKey,
      location: payload.location,
      schoolName: payload.schoolName,
      schoolCode: payload.schoolCode,
      class: payload.memberClass,
      memo: payload.memo,
    };

    setSignupMessage('');
    setIsSignupSubmitting(true);

    try {
      await authAPI.signup(signupPayload);
      setSignupMessage('회원가입 성공! 이제 로그인 탭에서 로그인해 주세요.');
      setActiveTab('login');
    } catch (error: any) {
      const serverMessage = extractErrorMessage(error);
      const lower = serverMessage.toLowerCase();
      if (
        lower.includes('already') ||
        lower.includes('exists') ||
        lower.includes('duplicate') ||
        serverMessage.includes('중복')
      ) {
        setSignupMessage(`중복가입 차단: ${serverMessage}`);
      } else {
        setSignupMessage(`회원가입 실패: ${serverMessage}`);
      }
    } finally {
      setIsSignupSubmitting(false);
    }
  };

  const handleRoleChange = (nextRole: MemberRole) => {
    setRole(nextRole);
    if (nextRole === 'admin') {
      setSelectedLocation('');
      setSelectedSchoolCode('');
    }
  };

  if (isLoading) {
    return <div className="auth-loading">로그인 상태를 확인하고 있습니다...</div>;
  }

  return (
    <main className="auth-page">
      <section className="auth-card">
        <p className="auth-eyebrow">WELCOME</p>
        <h1>환영합니다</h1>
        <p className="auth-description">로그인 또는 회원가입을 선택해 진행해 주세요.</p>

        <div className="auth-tab-row" role="tablist" aria-label="인증 메뉴">
          <button
            type="button"
            className={
              activeTab === 'login'
                ? 'auth-tab-button auth-tab-login-button active'
                : 'auth-tab-button auth-tab-login-button'
            }
            onClick={() => setActiveTab('login')}
          >
            로그인
          </button>
          <button
            type="button"
            className={
              activeTab === 'signup'
                ? 'auth-tab-button auth-tab-signup-button active'
                : 'auth-tab-button auth-tab-signup-button'
            }
            onClick={() => setActiveTab('signup')}
          >
            회원가입
          </button>
        </div>

        {activeTab === 'login' ? (
          <form className="auth-form" onSubmit={handleLoginSubmit}>
            <label className="auth-field">
              <span>이메일</span>
              <input
                type="email"
                value={loginEmail}
                onChange={(event) => setLoginEmail(event.target.value)}
                placeholder="예: user@example.com"
                required
              />
            </label>

            <label className="auth-field">
              <span>비밀번호</span>
              <input
                type="password"
                value={loginPassword}
                onChange={(event) => setLoginPassword(event.target.value)}
                placeholder="비밀번호를 입력하세요"
                required
              />
            </label>

            {loginMessage ? <p className="auth-inline-message">{loginMessage}</p> : null}

            <button
              className="auth-primary-button auth-login-button"
              type="submit"
              disabled={isLoginSubmitting}
            >
              {isLoginSubmitting ? '로그인 중...' : '로그인'}
            </button>
          </form>
        ) : (
          <form className="auth-form" onSubmit={handleSignupSubmit}>
            <div className="auth-role-row">
              <button
                type="button"
                className={role === 'admin' ? 'role-chip role-chip-active' : 'role-chip'}
                onClick={() => handleRoleChange('admin')}
              >
                관리자
              </button>
              <button
                type="button"
                className={role === 'student' ? 'role-chip role-chip-active' : 'role-chip'}
                onClick={() => handleRoleChange('student')}
              >
                학생
              </button>
            </div>

            <label className="auth-field">
              <span>이메일</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="예: user@example.com"
                required
              />
            </label>

            <label className="auth-field">
              <span>비밀번호</span>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="비밀번호를 입력하세요"
                required
              />
            </label>

            <label className="auth-field">
              <span>소속학원명</span>
              <input
                type="text"
                value={academyName}
                onChange={(event) => setAcademyName(event.target.value)}
                placeholder="학원명을 입력하세요"
                required
              />
            </label>

            <label className="auth-field">
              <span>이름</span>
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="이름을 입력하세요"
                required
              />
            </label>

            <label className="auth-field">
              <span>{role === 'student' ? '핸드폰 번호(학생)' : '핸드폰 번호'}</span>
              <input
                type="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                placeholder="예: 01012345678"
                required
              />
            </label>

            {role === 'student' ? (
              <label className="auth-field">
                <span>핸드폰 번호(보호자)</span>
                <input
                  type="tel"
                  value={parentPhone}
                  onChange={(event) => setParentPhone(event.target.value)}
                  placeholder="예: 01098765432"
                  required
                />
              </label>
            ) : null}

            <label className="auth-field">
              <span>반편성</span>
              <input
                type="text"
                value={memberClass}
                onChange={(event) => setMemberClass(event.target.value)}
                placeholder="예: A반, 고급반"
              />
            </label>

            <label className="auth-field">
              <span>메모</span>
              <input
                type="text"
                value={memo}
                onChange={(event) => setMemo(event.target.value)}
                placeholder="메모를 입력하세요"
              />
            </label>

            {role === 'student' ? (
              <>
                <label className="auth-field">
                  <span>학교 소재지역</span>
                  <select
                    value={selectedLocation}
                    onChange={(event) => {
                      setSelectedLocation(event.target.value);
                      setSelectedSchoolCode('');
                    }}
                    required
                  >
                    <option value="">학교 소재지역을 선택하세요</option>
                    {locations.map((location) => (
                      <option key={location} value={location}>
                        {location}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="auth-field">
                  <span>학교</span>
                  <select
                    value={selectedSchoolCode}
                    onChange={(event) => setSelectedSchoolCode(event.target.value)}
                    disabled={!selectedLocation}
                    required
                  >
                    <option value="">학교를 선택하세요</option>
                    {filteredSchools.map((school) => (
                      <option key={`${school.code}-${school.schoolName}`} value={school.code}>
                        {school.schoolName}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            ) : null}

            {signupMessage ? <p className="auth-inline-message">{signupMessage}</p> : null}

            <button
              className="auth-primary-button auth-signup-button"
              type="submit"
              disabled={isSignupSubmitting}
            >
              {isSignupSubmitting ? '가입 중...' : '회원가입'}
            </button>
          </form>
        )}
      </section>
    </main>
  );
};

export default WelcomePage;
